import { useEffect, useState } from "react";
import { Plus, RefreshCw, Pencil, Trash2, TrendingUp } from "lucide-react";
import { api } from "./api";
import {
  assetClasses,
  portfolioSummary,
  type InvestmentAsset,
  type InvestmentEntry,
  type Portfolio,
  type PortfolioRecord,
} from "./investments";
import { todayKey } from "./income-plan";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
const money = (n: number | null) =>
  n === null
    ? "Sem cotação"
    : new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(n);
const number = (n: number) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 8 }).format(n);
const numeric = (s: string) => Number(s.replace(",", "."));
const when = (s: string) => new Date(s).toLocaleString("pt-BR");
function AssetForm({
  item,
  save,
  close,
}: {
  item?: InvestmentAsset;
  save: (a: InvestmentAsset) => Promise<void>;
  close: () => void;
}) {
  const [symbol, setSymbol] = useState(item?.symbol || ""),
    [name, setName] = useState(item?.name || ""),
    [type, setType] = useState(item?.assetClass || "stock");
  const [price, setPrice] = useState(
      item?.price == null ? "" : String(item.price),
    ),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [quoteDate, setQuoteDate] = useState(
    item?.quotedAt?.slice(0, 10) || todayKey(),
  );
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && close()}>
      <DialogContent className="quick-modal">
        <DialogHeader>
          <DialogTitle>
            {item ? "Editar ativo e cotação" : "Adicionar ativo"}
          </DialogTitle>
          <DialogDescription>
            Todos os valores são em reais. Para renda fixa, você pode usar uma
            unidade e informar o valor atualizado da aplicação.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError("");
            try {
              const changed =
                price !== String(item?.price ?? "") ||
                quoteDate !== item?.quotedAt?.slice(0, 10);
              await save({
                id: item?.id || crypto.randomUUID(),
                symbol: symbol.trim().toUpperCase(),
                name,
                assetClass: type,
                price: price === "" ? null : numeric(price),
                quotedAt:
                  price === ""
                    ? null
                    : changed
                      ? new Date(quoteDate + "T12:00:00-03:00").toISOString()
                      : item?.quotedAt || new Date().toISOString(),
                quoteSource:
                  price === ""
                    ? null
                    : changed
                      ? "manual"
                      : item?.quoteSource || "manual",
              });
              close();
            } catch (e) {
              setError(e instanceof Error ? e.message : "Erro ao salvar");
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="form-grid">
            <div>
              <Label htmlFor="asset-symbol">Código / apelido</Label>
              <Input
                id="asset-symbol"
                required
                maxLength={24}
                placeholder="Ex.: PETR4"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
              />
            </div>
            <div>
              <Label>Classe</Label>
              <Select
                value={type}
                onValueChange={(v) => setType(v as typeof type)}
              >
                <SelectTrigger aria-label="Classe do ativo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(assetClasses).map(([id, label]) => (
                    <SelectItem key={id} value={id}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Label htmlFor="asset-name">Nome</Label>
          <Input
            id="asset-name"
            required
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome do investimento"
          />
          <Label htmlFor="asset-price">
            Cotação por unidade (R$, opcional)
          </Label>
          <Input
            id="asset-price"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
          <Label htmlFor="asset-date">Data dessa cotação</Label>
          <Input
            id="asset-date"
            type="date"
            required
            max={todayKey()}
            value={quoteDate}
            onChange={(e) => setQuoteDate(e.target.value)}
          />
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          <Button type="submit" variant="liquid" disabled={busy}>
            {busy ? "Salvando…" : "Salvar ativo"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
function EntryForm({
  assets,
  item,
  save,
  close,
}: {
  assets: InvestmentAsset[];
  item?: InvestmentEntry;
  save: (e: InvestmentEntry) => Promise<void>;
  close: () => void;
}) {
  const [asset, setAsset] = useState(item?.assetId || assets[0]?.id || ""),
    [kind, setKind] = useState(item?.kind || "buy");
  const [quantity, setQuantity] = useState(String(item?.quantity || 1)),
    [price, setPrice] = useState(item ? String(item.price) : ""),
    [fees, setFees] = useState(String(item?.fees || 0)),
    [date, setDate] = useState(item?.date || todayKey()),
    [notes, setNotes] = useState(item?.notes || "");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const total =
    numeric(quantity) * numeric(price) +
    (kind === "buy" ? numeric(fees) : kind === "sell" ? -numeric(fees) : 0);
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && close()}>
      <DialogContent className="quick-modal">
        <DialogHeader>
          <DialogTitle>
            {item ? "Editar lançamento" : "Lançamento de investimento"}
          </DialogTitle>
          <DialogDescription>
            Este registro acompanha sua carteira. Registre separadamente a
            movimentação bancária, quando necessário.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError("");
            try {
              await save({
                id: item?.id || crypto.randomUUID(),
                assetId: asset,
                kind,
                date,
                quantity: numeric(quantity),
                price: numeric(price),
                fees: kind === "dividend" ? 0 : numeric(fees),
                notes,
                createdAt: item?.createdAt || new Date().toISOString(),
              });
              close();
            } catch (e) {
              setError(e instanceof Error ? e.message : "Erro ao salvar");
            } finally {
              setBusy(false);
            }
          }}
        >
          <Label>Operação</Label>
          <Select value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
            <SelectTrigger aria-label="Operação">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="buy">Compra / aporte</SelectItem>
              <SelectItem value="sell">Venda / resgate</SelectItem>
              <SelectItem value="dividend">Provento</SelectItem>
            </SelectContent>
          </Select>
          <Label>Ativo</Label>
          <Select value={asset} onValueChange={setAsset}>
            <SelectTrigger aria-label="Ativo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {assets.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.symbol} · {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label htmlFor="entry-date">
            {kind === "dividend" ? "Data do recebimento" : "Data da operação"}
          </Label>
          <Input
            id="entry-date"
            type="date"
            required
            max={kind === "dividend" ? undefined : todayKey()}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <div className="form-grid">
            <div>
              <Label htmlFor="entry-quantity">Quantidade</Label>
              <Input
                id="entry-quantity"
                required
                inputMode="decimal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="entry-price">
                {kind === "dividend"
                  ? "Provento líquido por unidade"
                  : "Preço por unidade"}{" "}
                (R$)
              </Label>
              <Input
                id="entry-price"
                required
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
          </div>
          {kind !== "dividend" && (
            <>
              <Label htmlFor="entry-fees">Custos (R$)</Label>
              <Input
                id="entry-fees"
                required
                inputMode="decimal"
                value={fees}
                onChange={(e) => setFees(e.target.value)}
              />
            </>
          )}
          {kind === "dividend" && (
            <p className="form-notice">
              Para lançar um valor total, use quantidade 1. Datas futuras
              aparecem como provisionadas.
            </p>
          )}
          <Label htmlFor="entry-notes">Nota (opcional)</Label>
          <Input
            id="entry-notes"
            maxLength={500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="investment-total">
            Total <strong>{money(Number.isFinite(total) ? total : 0)}</strong>
          </div>
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          <Button type="submit" variant="liquid" disabled={busy || !asset}>
            {busy ? "Salvando…" : "Salvar lançamento"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
export function Investments() {
  const [record, setRecord] = useState<PortfolioRecord | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [assetForm, setAssetForm] = useState<InvestmentAsset | "new" | null>(
      null,
    ),
    [entryForm, setEntryForm] = useState<InvestmentEntry | "new" | null>(null),
    [deletion, setDeletion] = useState<{
      type: "asset" | "entry";
      id: string;
    } | null>(null);
  const load = async () => {
    try {
      setRecord(await api.investments());
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao carregar a carteira");
    }
  };
  useEffect(() => {
    void load();
  }, []);
  if (!record)
    return (
      <Card className="panel">
        <h2>Investimentos</h2>
        {error ? (
          <>
            <p role="alert">{error}</p>
            <Button onClick={() => void load()}>Tentar novamente</Button>
          </>
        ) : (
          "Carregando carteira…"
        )}
      </Card>
    );
  const { portfolio, version } = record,
    today = todayKey(),
    summary = portfolioSummary(portfolio, today);
  const save = async (next: Portfolio) => {
    let response;
    try {
      response = await api.saveInvestments(next, version);
    } catch (e) {
      if (e instanceof Error && e.message.includes("outra janela"))
        await load();
      throw e;
    }
    setRecord({ ...record, portfolio: next, version: response.version });
    toast.success("Carteira atualizada");
  };
  const dateLabel = (date: string) =>
    new Date(date + "T12:00:00").toLocaleDateString("pt-BR");
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(today + "T12:00:00");
    d.setDate(1);
    d.setMonth(d.getMonth() - 11 + i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return {
      key,
      label: d.toLocaleDateString("pt-BR", { month: "short" }),
      amount: portfolio.entries
        .filter(
          (e) =>
            e.kind === "dividend" && e.date <= today && e.date.startsWith(key),
        )
        .reduce((s, e) => s + e.quantity * e.price, 0),
    };
  });
  const entries = [...portfolio.entries].sort(
    (a, b) =>
      b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt),
  );
  const updateQuotes = async () => {
    setBusy(true);
    try {
      const {quotes,errors}=await api.investmentQuotes();
      const next=await api.investments();setRecord(next);
      if(quotes.length)toast.success('Cotações diárias carregadas',{description:'O preço pago nas suas compras permanece igual.'});
      if(errors.length)toast.info(errors.slice(0,3).map(e=>`${e.symbol}: ${e.message}`).join(' '));
      if(!quotes.length&&!errors.length)toast.info('Cadastre um ativo da B3 para consultar a cotação.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha na consulta");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="investments-view">
      <div className="module-heading">
        <div>
          <span className="eyebrow">SEU PATRIMÔNIO EM CONSTRUÇÃO</span>
          <h1>Minha carteira</h1>
          <p>Acompanhe seus ativos e o que eles geram ao longo do tempo.</p>
        </div>
        <div className="investment-actions">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => void updateQuotes()}
          >
            <RefreshCw /> Cotações
          </Button>
          <Button variant="outline" onClick={() => setAssetForm("new")}>
            <Plus /> Ativo
          </Button>
          <Button
            variant="liquid"
            disabled={!portfolio.assets.length}
            onClick={() => setEntryForm("new")}
          >
            <Plus /> Lançamento
          </Button>
        </div>
      </div>
      <div className="investment-metrics">
        {[
          ["Patrimônio atual", summary.value, "Quantidade × última cotação"],
          [
            "Custo das posições",
            summary.cost,
            "Compras e custos, descontadas as vendas",
          ],
          [
            "Resultado total",
            summary.profit,
            "Valorização + vendas + proventos",
          ],
          [
            "Proventos recebidos",
            summary.dividends,
            "Valores líquidos informados",
          ],
        ].map(([label, value, detail]) => (
          <Card className="metric-card" key={String(label)}>
            <span>{label}</span>
            <strong>{money(value as number | null)}</strong>
            <p>{detail}</p>
          </Card>
        ))}
      </div>
      <p className="investment-disclosure">
        Valores em BRL.{" "}
        {record.quotesEnabled
          ? "Cotações da B3 atualizadas uma vez ao dia, a partir das 20h de Brasília, nos dias úteis. O botão Cotações pode antecipar a consulta do dia. Aberturas repetidas usam o cache, sem gastar novas consultas."
          : "Cotação automática disponível para PETR4, VALE3, MGLU3 e ITUB4. Os demais ativos usam atualização manual até configurar o provedor."}{" "}
        O preço pago é editado no lançamento da compra; a cotação calcula a valorização. Renda fixa e cripto continuam com atualização manual.
      </p>
      <Tabs defaultValue="positions">
        <TabsList>
          <TabsTrigger value="positions">Posições</TabsTrigger>
          <TabsTrigger value="dividends">Proventos</TabsTrigger>
          <TabsTrigger value="entries">Lançamentos</TabsTrigger>
        </TabsList>
        <TabsContent value="positions">
          <div className="investment-layout">
            <Card className="panel">
              <div className="panel-head">
                <h2>Seus ativos</h2>
                <Badge variant="secondary">
                  {portfolio.assets.length} ativos
                </Badge>
              </div>
              {!portfolio.assets.length ? (
                <div className="investment-empty">
                  <TrendingUp />
                  <h3>Comece pelo seu primeiro ativo</h3>
                  <p>
                    Cadastre um investimento e registre a compra para acompanhar
                    quantidade, preço médio e resultado.
                  </p>
                  <Button variant="liquid" onClick={() => setAssetForm("new")}>
                    Adicionar ativo
                  </Button>
                </div>
              ) : (
                <div className="investment-assets">
                  {summary.rows.map((p) => (
                    <article className="investment-asset" key={p.asset.id}>
                      <div className="investment-asset-head">
                        <div>
                          <strong>{p.asset.symbol}</strong>
                          <span>
                            {p.asset.name} · {assetClasses[p.asset.assetClass]}
                          </span>
                        </div>
                        <div>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Editar ${p.asset.symbol}`}
                            onClick={() => setAssetForm(p.asset)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Excluir ${p.asset.symbol}`}
                            disabled={portfolio.entries.some(
                              (e) => e.assetId === p.asset.id,
                            )}
                            onClick={() =>
                              setDeletion({ type: "asset", id: p.asset.id })
                            }
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </div>
                      <dl>
                        <div>
                          <dt>Quantidade</dt>
                          <dd>{number(p.quantity)}</dd>
                        </div>
                        <div>
                          <dt>Preço médio</dt>
                          <dd>{money(p.average)}</dd>
                        </div>
                        <div>
                          <dt>Cotação</dt>
                          <dd>{money(p.asset.price)}</dd>
                        </div>
                        <div>
                          <dt>Valor atual</dt>
                          <dd>{money(p.value)}</dd>
                        </div>
                        <div>
                          <dt>Valorização</dt>
                          <dd
                            className={
                              (p.unrealized || 0) >= 0 ? "positive" : "negative"
                            }
                          >
                            {money(p.unrealized)}
                            {p.cost > 0 && p.unrealized !== null
                              ? ` (${((p.unrealized / p.cost) * 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%)`
                              : ""}
                          </dd>
                        </div>
                        <div>
                          <dt>Resultado em vendas</dt>
                          <dd>{money(p.realized)}</dd>
                        </div>
                      </dl>
                      <small>
                        {p.asset.quotedAt
                          ? `${p.asset.quoteSource === "brapi" ? "brapi · preço de mercado" : "Cotação manual"} · ${when(p.asset.quotedAt)}`
                          : "Informe uma cotação para calcular o valor atual."}
                      </small>
                      {record.quoteErrors?.filter(error => error.symbol === p.asset.symbol).map(error => (
                        <small className="form-notice" key={error.code}>{error.message} A última cotação disponível foi mantida.</small>
                      ))}
                    </article>
                  ))}
                </div>
              )}
            </Card>
            <Card className="panel investment-allocation">
              <h2>Distribuição</h2>
              <p>Participação no valor atual da carteira.</p>
              {summary.value === null ? (
                <p>Complete as cotações para visualizar a distribuição.</p>
              ) : summary.value === 0 ? (
                <p>Seus investimentos aparecerão aqui.</p>
              ) : (
                Object.entries(assetClasses).map(([key, label]) => {
                  const value = summary.rows
                      .filter((p) => p.asset.assetClass === key)
                      .reduce((s, p) => s + (p.value || 0), 0),
                    weight = (value / summary.value!) * 100;
                  return value > 0 ? (
                    <div className="allocation-row" key={key}>
                      <div>
                        <span>{label}</span>
                        <b>
                          {weight.toLocaleString("pt-BR", {
                            maximumFractionDigits: 1,
                          })}
                          %
                        </b>
                      </div>
                      <div className="allocation-track">
                        <span style={{ width: `${weight}%` }} />
                      </div>
                      <small>{money(value)}</small>
                    </div>
                  ) : null;
                })
              )}
              <hr />
              <h3>Como calculamos</h3>
              <p>
                Preço médio inclui custos de compra. Vendas reduzem o custo
                proporcionalmente; proventos ficam separados. A valorização
                percentual usa o custo da posição atual e não é uma
                rentabilidade histórica ponderada por tempo.
              </p>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="dividends">
          <div className="investment-metrics">
            {[
              ["Últimos 12 meses", months.reduce((s, m) => s + m.amount, 0)],
              ["Neste mês", months.at(-1)!.amount],
              [
                "Média mensal (12 meses)",
                months.reduce((s, m) => s + m.amount, 0) / 12,
              ],
              ["Provisionados", summary.provisioned],
            ].map(([label, value]) => (
              <Card className="metric-card" key={String(label)}>
                <span>{label}</span>
                <strong>{money(Number(value))}</strong>
              </Card>
            ))}
          </div>
          <Card className="panel">
            <h2>Proventos nos últimos 12 meses</h2>
            <div className="dividend-chart">
              {months.map((m) => (
                <div key={m.key} className="dividend-column">
                  <span>{money(m.amount)}</span>
                  <div>
                    <i
                      style={{
                        height: `${Math.max(2, (m.amount / Math.max(1, ...months.map((x) => x.amount))) * 100)}%`,
                      }}
                    />
                  </div>
                  <small>{m.label}</small>
                </div>
              ))}
            </div>
            <p className="investment-disclosure">
              Recebimentos futuros ficam provisionados. Os valores são
              registrados por você; proventos não são calculados pela variação
              da cotação.
            </p>
          </Card>
        </TabsContent>
        <TabsContent value="entries">
          <Card className="panel">
            <div className="panel-head">
              <h2>Histórico de lançamentos</h2>
              <Button
                disabled={!portfolio.assets.length}
                variant="outline"
                onClick={() => setEntryForm("new")}
              >
                <Plus /> Adicionar
              </Button>
            </div>
            {!entries.length ? (
              <p className="no-results">
                Nenhum lançamento. Adicione uma compra, venda ou provento.
              </p>
            ) : (
              entries.map((e) => (
                <div className="investment-entry" key={e.id}>
                  <div>
                    <strong>
                      {portfolio.assets.find((a) => a.id === e.assetId)?.symbol}{" "}
                      ·{" "}
                      {
                        { buy: "Compra", sell: "Venda", dividend: "Provento" }[
                          e.kind
                        ]
                      }
                    </strong>
                    <span>
                      {dateLabel(e.date)} · {number(e.quantity)} ×{" "}
                      {money(e.price)}
                      {e.fees ? ` · Custos ${money(e.fees)}` : ""}
                      {e.date > today ? " · Provisionado" : ""}
                    </span>
                    {e.notes && <small>{e.notes}</small>}
                  </div>
                  <b>
                    {money(
                      e.quantity * e.price +
                        (e.kind === "buy" ? e.fees : -e.fees),
                    )}
                  </b>
                  <div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Editar lançamento"
                      onClick={() => setEntryForm(e)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Excluir lançamento"
                      onClick={() => setDeletion({ type: "entry", id: e.id })}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </Card>
        </TabsContent>
      </Tabs>
      {assetForm && (
        <AssetForm
          item={assetForm === "new" ? undefined : assetForm}
          close={() => setAssetForm(null)}
          save={async (a) =>
            save({
              ...portfolio,
              assets: portfolio.assets.some((x) => x.id === a.id)
                ? portfolio.assets.map((x) => (x.id === a.id ? a : x))
                : [...portfolio.assets, a],
            })
          }
        />
      )}
      {entryForm && (
        <EntryForm
          assets={portfolio.assets}
          item={entryForm === "new" ? undefined : entryForm}
          close={() => setEntryForm(null)}
          save={async (e) =>
            save({
              ...portfolio,
              entries: portfolio.entries.some((x) => x.id === e.id)
                ? portfolio.entries.map((x) => (x.id === e.id ? e : x))
                : [...portfolio.entries, e],
            })
          }
        />
      )}
      <Dialog
        open={!!deletion}
        onOpenChange={(v) => !v && !busy && setDeletion(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Excluir {deletion?.type === "asset" ? "ativo" : "lançamento"}?
            </DialogTitle>
            <DialogDescription>
              Os indicadores serão recalculados. Não será possível desfazer esta
              exclusão. Uma compra necessária para cobrir uma venda não pode ser
              removida.
            </DialogDescription>
          </DialogHeader>
          <div className="dialog-actions">
            <Button variant="outline" onClick={() => setDeletion(null)}>
              Voltar
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={async () => {
                if (!deletion) return;
                setBusy(true);
                try {
                  await save(
                    deletion.type === "asset"
                      ? {
                          ...portfolio,
                          assets: portfolio.assets.filter(
                            (a) => a.id !== deletion.id,
                          ),
                        }
                      : {
                          ...portfolio,
                          entries: portfolio.entries.filter(
                            (e) => e.id !== deletion.id,
                          ),
                        },
                  );
                  setDeletion(null);
                } catch (e) {
                  toast.error(
                    e instanceof Error ? e.message : "Falha ao excluir",
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              Excluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
